import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  attachContractToOrder,
  createContractTemplate,
  detachContractFromOrder,
  getContractTemplate,
  getCurrentTemplateVersion,
  getOrderContractStatus,
  listContractTemplates,
  listContractTemplatesTop,
  listOrderAcceptances,
  listProposalContracts,
  listTemplateVersions,
  publicGetProposalContracts,
  publishTemplateVersion,
  updateContractTemplate,
  type ActiveContext,
  type AttachContractInput,
  type CreateContractTemplateInput,
  type KeysetCursor,
  type ListContractTemplatesInput,
  type PublishTemplateVersionInput,
  type UpdateContractTemplateInput,
} from '@pc/db';
import { appBus } from '@pc/core';

const K = ['contracts'] as const;

/**
 * One-shot bounded list of contract templates. Used by dashboard widgets.
 * The main list page uses `useInfiniteContractTemplates`.
 */
export function useContractTemplates(
  ctx: ActiveContext,
  input: Omit<ListContractTemplatesInput, 'cursor' | 'countMode' | 'pageSize'> & {
    limit?: number;
  } = {},
) {
  return useQuery({
    queryKey: [...K, ctx.orgId, ctx.storeId, 'top', input],
    queryFn: () => listContractTemplatesTop(ctx, input),
  });
}

/**
 * Keyset-paginated infinite list of contract templates. Drives
 * `ContractsListPage`.
 */
export function useInfiniteContractTemplates(
  ctx: ActiveContext,
  input: Omit<ListContractTemplatesInput, 'cursor' | 'pageSize'> = {},
) {
  return useInfiniteQuery({
    queryKey: [...K, ctx.orgId, ctx.storeId, 'infinite', input],
    queryFn: ({ pageParam }) =>
      listContractTemplates(ctx, {
        ...input,
        cursor: pageParam ?? null,
        countMode: pageParam ? 'none' : (input.countMode ?? 'estimated'),
      }),
    initialPageParam: null as KeysetCursor | null,
    getNextPageParam: (last) => last.nextCursor,
  });
}

export function useContractTemplate(ctx: ActiveContext, id: string | undefined) {
  return useQuery({
    queryKey: [...K, ctx.orgId, 'one', id],
    queryFn: () => getContractTemplate(ctx, id as string),
    enabled: Boolean(id),
  });
}

export function useTemplateVersions(ctx: ActiveContext, templateId: string | undefined) {
  return useQuery({
    queryKey: [...K, 'versions', ctx.orgId, templateId],
    queryFn: () => listTemplateVersions(ctx, templateId as string),
    enabled: Boolean(templateId),
  });
}

export function useCurrentTemplateVersion(ctx: ActiveContext, templateId: string | undefined) {
  return useQuery({
    queryKey: [...K, 'current-version', ctx.orgId, templateId],
    queryFn: () => getCurrentTemplateVersion(ctx, templateId as string),
    enabled: Boolean(templateId),
  });
}

export function useCreateContractTemplate(ctx: ActiveContext) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateContractTemplateInput) => createContractTemplate(ctx, input),
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: K });
      appBus().emit('contract.template_created', { id: row.id, name: row.name });
    },
  });
}

export function useUpdateContractTemplate(ctx: ActiveContext) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateContractTemplateInput) => updateContractTemplate(ctx, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: K }),
  });
}

export function usePublishTemplateVersion(ctx: ActiveContext) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PublishTemplateVersionInput) => publishTemplateVersion(ctx, input),
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: K });
      appBus().emit('contract.template_published', {
        id: row.id,
        templateId: row.template_id,
        versionNumber: row.version_number,
      });
    },
  });
}

export function useProposalContracts(ctx: ActiveContext, orderId: string | undefined) {
  return useQuery({
    queryKey: [...K, 'proposal', ctx.orgId, orderId],
    queryFn: () => listProposalContracts(ctx, orderId as string),
    enabled: Boolean(orderId),
  });
}

export function useOrderContractStatus(ctx: ActiveContext, orderId: string | undefined) {
  return useQuery({
    queryKey: [...K, 'status', ctx.orgId, orderId],
    queryFn: () => getOrderContractStatus(ctx, orderId as string),
    enabled: Boolean(orderId),
  });
}

export function useOrderAcceptances(ctx: ActiveContext, orderId: string | undefined) {
  return useQuery({
    queryKey: [...K, 'acceptances', ctx.orgId, orderId],
    queryFn: () => listOrderAcceptances(ctx, orderId as string),
    enabled: Boolean(orderId),
  });
}

export function useAttachContractToOrder(ctx: ActiveContext) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AttachContractInput) => attachContractToOrder(ctx, input),
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: K });
      appBus().emit('contract.attached_to_order', {
        id: row.id,
        orderId: row.order_id,
        templateId: row.template_id,
      });
    },
  });
}

export function useDetachContractFromOrder(ctx: ActiveContext) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, orderId: _orderId }: { id: string; orderId: string }) =>
      detachContractFromOrder(ctx, id),
    onSuccess: (_void, vars) => {
      qc.invalidateQueries({ queryKey: K });
      appBus().emit('contract.detached_from_order', { id: vars.id, orderId: vars.orderId });
    },
  });
}

export function usePublicProposalContracts(token: string | undefined) {
  return useQuery({
    queryKey: ['contracts', 'public', token],
    queryFn: () => publicGetProposalContracts(token as string),
    enabled: Boolean(token),
  });
}
